import React, { useMemo } from "react";
import { BodyMediumText } from "../FormattedText";
import SelectField from "../SelectField";
import { Contract, Repetition, Unit } from "../../__generated__/graphql";
import { useTheme } from "../../hooks/use-theme";
import { useI18n } from "../../hooks/i18n-hooks";
import {
    generateHoursPerWeekSelection,
    MAX_HOURS_PER_WEEK_FULL_TIME,
    MIN_HOURS_PER_WEEK_FULL_TIME,
    WorkingTimeComponentProps,
} from "./helpers";
import { useContractCct } from "../../hooks/cct-utils";
import Gap from "../../ui_atoms/Gap";
import { ClassicModeType } from "./MonthlyClassicComponent";
import { DEFAULT_HOURS_PER_WEEK } from "../../utils";

type HoursPerWeekSectionProps = WorkingTimeComponentProps & {
    mode: ClassicModeType;
};

const HoursPerWeekSection: React.FC<HoursPerWeekSectionProps> = ({
    contract,
    setContract,
    disabled,
    mode,
}) => {
    const { colors, gridSize } = useTheme();
    const i18n = useI18n();
    const currentCCT = useContractCct(contract as Contract);

    const hoursItems = useMemo(() => {
        const hoursPerWeekDefault = currentCCT?.hoursPerWeekDefault || DEFAULT_HOURS_PER_WEEK;

        const maxHoursPerWeek = currentCCT?.hoursPerWeek || MAX_HOURS_PER_WEEK_FULL_TIME;
        const hours = generateHoursPerWeekSelection(
            MIN_HOURS_PER_WEEK_FULL_TIME,
            maxHoursPerWeek,
            hoursPerWeekDefault,
        );

        return hours.map((hour) => {
            return {
                label: i18n.t("screens.user.create_mission_earning_type.working_percent_format", {
                    hours: hour,
                }),
                value: hour,
            };
        });
    }, [currentCCT?.hoursPerWeekDefault, currentCCT?.hoursPerWeek, i18n]);

    return (
        <>
            <BodyMediumText color={colors.body}>
                {i18n.t("screens.user.create_mission_earning_type.hours_per_week")}
            </BodyMediumText>
            <Gap gap="2/3" />
            <SelectField
                asListAction="primary"
                disabled={disabled}
                onSelect={({ value: newValue }) => {
                    let newUnitCount: number | undefined;

                    if (contract.workingTimeClassicSpec?.unitCount) {
                        if (mode === "percent") {
                            // Get initial specs to
                            // 1) retreive inital percentage, then
                            // 2) calculate new unit count based on new value (hours per week) and this percentage

                            const initialUnitCount = contract.workingTimeClassicSpec.unitCount;
                            const initialHoursPerWeekFullTime = contract.hoursPerWeekFullTime || 0;
                            const initialPercentage =
                                initialUnitCount / initialHoursPerWeekFullTime;
                            newUnitCount = Math.round(initialPercentage * newValue * 100) / 100;
                        } else if (mode === "timePerWeek") {
                            // If timePerWeek mode, don't change the already-entered value in unitCount

                            newUnitCount = contract.workingTimeClassicSpec.unitCount;
                        }
                    } else {
                        // If field has no value, directly take the newValue
                        newUnitCount = newValue;
                    }

                    setContract({
                        ...contract,
                        hoursPerWeekFullTime: newValue,
                        workingTimeClassicSpec: {
                            unitCount: newUnitCount,
                            unit: Unit.Hours,
                            repetition: Repetition.Week,
                        },
                    });
                }}
                data={hoursItems}
                value={contract?.hoursPerWeekFullTime || 0}
                right="edit"
                style={{
                    marginHorizontal: -gridSize,
                    paddingHorizontal: gridSize,
                    borderBottomWidth: 0,
                    borderTopWidth: 1,
                }}
                placeholder={i18n.t(
                    "screens.user.create_mission_earning_type.hours_per_week_placeholder",
                )}
            />
        </>
    );
};

export default HoursPerWeekSection;
